import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { User } from "../messaging/types"
import UserManagementPage from "../messaging/UserSelection"
import { InformationSource, Profession, VisitLog, CreateVisitorDto, Visitor, VisitorType, VisitPurpose, CreateVisitLogDto, Member, FollowUpStatus } from "./visitor-dto"
import { VisitorApi } from "./visitor-api"
import { VistorList } from "./VisitorList"

// Helper function to format ISO datetime string to datetime-local format (YYYY-MM-DDTHH:mm)
const formatDateTimeLocal = (isoString: string | null | undefined): string => {
    if (!isoString) return ''
    try {
        const date = new Date(isoString)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day}T${hours}:${minutes}`
    } catch (e) {
        return ''
    }
}

// Vehicle Form Component
const VehicleForm = ({ vehicle, onSave, onClose }: { 
    vehicle?: { reg_no: string; type: string; color?: string; make?: string }, 
    onSave: (data: { reg_no: string; type: string; color?: string; make?: string }) => void,
    onClose: () => void 
}) => {
    const [formData, setFormData] = useState({
        reg_no: vehicle?.reg_no || '',
        type: vehicle?.type || '',
        color: vehicle?.color || '',
        make: vehicle?.make || ''
    })

    // Reset form when vehicle prop changes
    useEffect(() => {
        setFormData({
            reg_no: vehicle?.reg_no || '',
            type: vehicle?.type || '',
            color: vehicle?.color || '',
            make: vehicle?.make || ''
        })
    }, [vehicle])

    const vehicleTypes = [
        { value: "car", label: "Car" },
        { value: "bike", label: "Bike" },
        { value: "truck", label: "Truck" },
        { value: "motorcycle", label: "Motorcycle" },
        { value: "bus", label: "Bus" },
        { value: "van", label: "Van" },
        { value: "other", label: "Other" },
    ]

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!formData.reg_no.trim()) {
            alert("Registration number is required")
            return
        }
        if (!formData.type) {
            alert("Vehicle type is required")
            return
        }
        onSave(formData)
    }

    return (
        <form onSubmit={handleSubmit}>
            <div className="space-y-4">
                <div>
                    <label htmlFor="reg_no" className="block text-sm text-black dark:text-white mb-1">
                        Registration Number <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="reg_no"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                        value={formData.reg_no}
                        onChange={(e) => setFormData({ ...formData, reg_no: e.target.value.toUpperCase() })}
                        required
                    />
                </div>
                <div>
                    <label htmlFor="type" className="block text-sm text-black dark:text-white mb-1">
                        Vehicle Type <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="type"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        required
                    >
                        <option value="">Select vehicle type</option>
                        {vehicleTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="make" className="block text-sm text-black dark:text-white mb-1">
                        Make/Model (Optional)
                    </label>
                    <input
                        type="text"
                        id="make"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                        value={formData.make}
                        onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                        placeholder="e.g., Toyota Camry"
                    />
                </div>
                <div>
                    <label htmlFor="color" className="block text-sm text-black dark:text-white mb-1">
                        Color (Optional)
                    </label>
                    <input
                        type="text"
                        id="color"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        placeholder="e.g., Silver, Black"
                    />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 bg-neutral-800 text-white rounded-md hover:bg-neutral-700"
                    >
                        {vehicle ? 'Update' : 'Add'} Vehicle
                    </button>
                </div>
            </div>
        </form>
    )
}

export const VisitLogEdit = ({ onClose, onSuccess, visitLog }: { onClose: () => void, onSuccess: () => void, visitLog?: VisitLog }) => {

    const [showSelectMember, setShowSelectMember] = useState(false)
    const [selectedUser, setSelectedUser] = useState<User>()
    const [showSelectToMeet, setShowSelectToMeet] = useState(false)
    const [selectToMeet, setSelectToMeet] = useState<User[]>()
    const [referrer, setReferrer] = useState<User>()
    const [showReferrer, setShowReferrer] = useState(false)

    const [requestVisitLogData, setAddRequestVisitData] = useState<CreateVisitLogDto>({} as CreateVisitLogDto)
    const [requestVisitorData, setAddRequestVisitorData] = useState<CreateVisitorDto>({} as CreateVisitorDto)
    const [showVisitor, setShowVisitor] = useState(false)
    const [existingVisitor, setExistingVisitor] = useState<Visitor>()

    const [phones, setPhones] = useState<string[]>([''])
    const [emails, setEmails] = useState<string[]>([''])
    const [vehicles, setVehicles] = useState<Array<{ reg_no: string; type: string; color?: string; make?: string }>>([])
    const [showVehicleForm, setShowVehicleForm] = useState(false)
    const [requireFollowUp, setRequireFollowUp] = useState(false)
    const [followUpStatus, setFollowUpStatus] = useState<number>(FollowUpStatus.LEAD) // Default to LEAD

    useEffect(() => {
        if (visitLog) {
            // Load main visit log data
            const entryTime = visitLog.entry_time || visitLog.check_in
            const exitTime = visitLog.exit_time || visitLog.check_out
            
            const logData: CreateVisitLogDto = {
                notes: visitLog.notes || '',
                purpose: visitLog.purpose,
                visitor_type: visitLog.visitory_type,
                to_meet_user_ids: visitLog.to_meet?.map(t => t.id) || [],
                entry_time: entryTime || '',
                exit_time: exitTime || '',
                checkin_time: formatDateTimeLocal(entryTime),
                checkout_time: formatDateTimeLocal(exitTime)
            }

            // Set visitor or member ID (use user_id as primary, member_id as alias)
            if (visitLog.is_member && visitLog.member) {
                logData.user_id = visitLog.member.id
                logData.member_id = visitLog.member.id // Also set alias for compatibility
                setSelectedUser(visitLog.member as any as User)
            } else if (visitLog.visitor) {
                logData.visitor_id = visitLog.visitor.id
                setExistingVisitor(visitLog.visitor)
            }

            setAddRequestVisitData(logData)

            // Load "to meet" members
            if (visitLog.to_meet && visitLog.to_meet.length > 0) {
                setSelectToMeet(visitLog.to_meet as any as User[])
            }

            // Load visitor details if it's a visitor (not a member)
            if (visitLog.visitor && !visitLog.is_member) {
                const visitor = visitLog.visitor
                const visitorData: CreateVisitorDto = {
                    visitor_full_name: visitor.visitor_full_name || '',
                    designation_profession: visitor.designation_profession,
                    other_designation_profession: visitor.other_designation_profession || '',
                    source_of_information: visitor.source_of_information,
                    other_source_specify: visitor.other_source_specify || '',
                    current_church: visitor.current_church || '',
                    current_church_pastor: Array.isArray(visitor.current_church_pastors) 
                        ? visitor.current_church_pastors.join(', ')
                        : (visitor.current_church_pastors as string) || '',
                    contact_preference: visitor.contact_preference,
                    prayer_request: visitor.prayer_request || '',
                    is_referrer_member: visitor.is_referrer_member || false,
                    referrer_full_name: visitor.referrer_full_name || '',
                    referrer_address: visitor.referrer_address || '',
                    referrer_phone: visitor.referrer_phone || '',
                    referrer_member_user_id: visitor.referrer_member_user_id || undefined
                }

                // Load phone numbers
                if (visitor.phones && visitor.phones.length > 0) {
                    setPhones(visitor.phones)
                } else if (visitor.phone) {
                    setPhones([visitor.phone])
                } else {
                    setPhones([''])
                }

                // Load email addresses
                if (visitor.emails && visitor.emails.length > 0) {
                    setEmails(visitor.emails)
                } else if (visitor.email) {
                    setEmails([visitor.email])
                } else {
                    setEmails([''])
                }

                setAddRequestVisitorData(visitorData)
            }

            // Load vehicles from visit log if they exist
            if (visitLog.vehicles && visitLog.vehicles.length > 0) {
                const vehicleData = visitLog.vehicles.map((v: any) => ({
                    reg_no: v.reg_no || '',
                    type: v.type || '',
                    color: v.color || '',
                    make: v.make || v.model || ''
                }))
                setVehicles(vehicleData)
            }

            // Load follow-up information if it exists
            if ((visitLog as any).require_follow_up !== undefined) {
                setRequireFollowUp((visitLog as any).require_follow_up || false)
                if ((visitLog as any).follow_up_status !== undefined) {
                    setFollowUpStatus((visitLog as any).follow_up_status)
                }
            }
        }
    }, [visitLog])

    const handleFormVisitData = (propName: string, value: any) => {
        setAddRequestVisitData((r) => {
            return { ...r, [propName]: value }
        })
    }

    const handleFormVisitorData = (propName: string, value: any) => {
        setAddRequestVisitorData((r) => {
            return { ...r, [propName]: value }
        })
    }
    useEffect(() => {
        // alert(JSON.stringify(requestData))
    }, [requestVisitLogData])

    function handleSubmit() {
        // Validation: Required fields for visit log
        if (requestVisitLogData.purpose === undefined || requestVisitLogData.purpose < 0) {
            toast.error("Please select a visit purpose")
            return
        }

        if (requestVisitLogData.visitor_type === undefined || requestVisitLogData.visitor_type < 0) {
            toast.error("Please select a visitor type")
            return
        }

        // Validation: Must have either member, existing visitor, or new visitor data
        const hasMember = !!(selectedUser || requestVisitLogData.user_id || requestVisitLogData.member_id)
        const hasExistingVisitor = !!(existingVisitor || requestVisitLogData.visitor_id)
        const hasNewVisitorData = !!(requestVisitorData.visitor_full_name && requestVisitorData.visitor_full_name.trim())

        if (!hasMember && !hasExistingVisitor && !hasNewVisitorData) {
            toast.error("Please select a member, existing visitor, or enter visitor details")
            return
        }

        // Validation: If creating new visitor, validate required visitor fields
        if (!hasMember && !hasExistingVisitor) {
            // Validate visitor full name
            if (!requestVisitorData.visitor_full_name || !requestVisitorData.visitor_full_name.trim()) {
                toast.error("Visitor full name is required")
                return
            }

            // Validate designation/profession
            if (requestVisitorData.designation_profession === undefined || requestVisitorData.designation_profession < 0) {
                toast.error("Please select a designation/profession")
                return
            }

            // If "Other" is selected for designation, validate other_designation_profession
            if (requestVisitorData.designation_profession === Profession.OTHER) {
                if (!requestVisitorData.other_designation_profession || !requestVisitorData.other_designation_profession.trim()) {
                    toast.error("Please specify the designation/profession")
                    return
                }
            }

            // Validate source of information
            if (requestVisitorData.source_of_information === undefined || requestVisitorData.source_of_information < 0) {
                toast.error("Please select a source of information")
                return
            }

            // If "Other" is selected for source, validate other_source_specify
            if (requestVisitorData.source_of_information === InformationSource.OTHER) {
                if (!requestVisitorData.other_source_specify || !requestVisitorData.other_source_specify.trim()) {
                    toast.error("Please specify the source of information")
                    return
                }
            }

            // Validate at least one phone number or email
            const validPhones = phones.filter(p => p.trim() !== '')
            const validEmails = emails.filter(e => e.trim() !== '')
            if (validPhones.length === 0 && validEmails.length === 0) {
                toast.error("Please provide at least one phone number or email address")
                return
            }

            // Validate phone numbers format (basic validation)
            for (const phone of validPhones) {
                if (phone.trim().length < 10) {
                    toast.error("Please enter a valid phone number (at least 10 digits)")
                    return
                }
            }

            // Validate email format (basic validation)
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            for (const email of validEmails) {
                if (!emailRegex.test(email.trim())) {
                    toast.error(`Please enter a valid email address: ${email}`)
                    return
                }
            }
        }

        // Validation: If "Require Follow Up" is checked, follow_up_status must be set
        if (requireFollowUp) {
            if (followUpStatus === undefined || followUpStatus < 0) {
                toast.error("Please select a follow-up status")
                return
            }
        }

        // Validation: Check-in time should be provided
        if (!requestVisitLogData.checkin_time && !requestVisitLogData.entry_time) {
            toast.error("Please provide a check-in time")
            return
        }

        // Validation: If checkout time is provided, it should be after check-in time
        if (requestVisitLogData.checkout_time && requestVisitLogData.checkin_time) {
            const checkin = new Date(requestVisitLogData.checkin_time)
            const checkout = new Date(requestVisitLogData.checkout_time)
            if (checkout <= checkin) {
                toast.error("Check-out time must be after check-in time")
                return
            }
        }

        requestVisitLogData.to_meet_user_ids = (selectToMeet || []).map(u => u.id)

        // Map checkin_time to entry_time if needed
        if (requestVisitLogData.checkin_time && !requestVisitLogData.entry_time) {
            requestVisitLogData.entry_time = requestVisitLogData.checkin_time
        }
        // Map checkout_time to exit_time if needed
        if (requestVisitLogData.checkout_time && !requestVisitLogData.exit_time) {
            requestVisitLogData.exit_time = requestVisitLogData.checkout_time
        }

        if (!requestVisitLogData.user_id && !requestVisitLogData.visitor_id) {

            requestVisitorData.phone_numbers = phones.filter(p => p.trim() !== '')
            requestVisitorData.email_addresses = emails.filter(e => e.trim() !== '')

            // Ensure is_referrer_member is set correctly
            if (referrer) {
                requestVisitorData.is_referrer_member = true
            } else if (requestVisitorData.referrer_member_user_id) {
                requestVisitorData.is_referrer_member = true
            } else {
                requestVisitorData.is_referrer_member = false
            }

            // Ensure current_church_pastor is set from church_pastors if needed
            if ((requestVisitorData as any)['church_pastors'] && !requestVisitorData.current_church_pastor) {
                requestVisitorData.current_church_pastor = (requestVisitorData as any)['church_pastors']
            }

            requestVisitLogData.visitor = requestVisitorData
        }

        // Add vehicles data to the request (do this last to ensure it's not overwritten)
        // Format vehicles to ensure they match API requirements
        // Only include vehicles with valid registration numbers
        const validVehicles = vehicles
            .filter(v => v.reg_no && v.reg_no.trim().length > 0) // Filter out invalid vehicles first
            .map(v => {
                const formatted: { reg_no: string; type?: string; make?: string; color?: string } = {
                    reg_no: v.reg_no.trim() // Required - ensure trimmed
                }
                // Only include optional fields if they have values
                if (v.type && v.type.trim()) {
                    formatted.type = v.type.trim()
                }
                if (v.make && v.make.trim()) {
                    formatted.make = v.make.trim()
                }
                if (v.color && v.color.trim()) {
                    formatted.color = v.color.trim()
                }
                return formatted
            })
        
        // Always set vehicles array if there are valid vehicles
        if (validVehicles.length > 0) {
            requestVisitLogData.vehicles = validVehicles
            console.log("Adding vehicles to request:", validVehicles)
        } else {
            // Clear vehicles if none are valid (don't send empty array unless needed)
            delete requestVisitLogData.vehicles
            if (vehicles.length > 0) {
                console.warn("No valid vehicles found. Original vehicles:", vehicles)
            }
        }

        // Add follow-up information if required
        if (requireFollowUp) {
            requestVisitLogData.require_follow_up = true
            requestVisitLogData.follow_up_status = followUpStatus
        }

        // Ensure user_id is set when member is selected (member_id is alias)
        if (selectedUser) {
            requestVisitLogData.user_id = selectedUser.id
            requestVisitLogData.member_id = selectedUser.id // Also set alias
        }

        // Convert datetime-local strings to ISO format if needed
        if (requestVisitLogData.checkin_time && typeof requestVisitLogData.checkin_time === 'string' && requestVisitLogData.checkin_time.includes('T') && !requestVisitLogData.checkin_time.includes('Z')) {
            // Convert datetime-local format to ISO
            const checkinDate = new Date(requestVisitLogData.checkin_time)
            if (!isNaN(checkinDate.getTime())) {
                requestVisitLogData.checkin_time = checkinDate.toISOString()
                requestVisitLogData.entry_time = checkinDate.toISOString()
            }
        }
        
        if (requestVisitLogData.checkout_time && typeof requestVisitLogData.checkout_time === 'string' && requestVisitLogData.checkout_time.includes('T') && !requestVisitLogData.checkout_time.includes('Z')) {
            const checkoutDate = new Date(requestVisitLogData.checkout_time)
            if (!isNaN(checkoutDate.getTime())) {
                requestVisitLogData.checkout_time = checkoutDate.toISOString()
                requestVisitLogData.exit_time = checkoutDate.toISOString()
            }
        }

        // Debug: Log the data being sent
        console.log("Visit Log Data being sent:", JSON.stringify(requestVisitLogData, null, 2))
        console.log("Vehicles in request:", JSON.stringify(requestVisitLogData.vehicles, null, 2))
        console.log("All Form Data Summary:", {
            visitLogData: requestVisitLogData,
            visitorData: requestVisitorData,
            vehicles: vehicles,
            vehiclesInRequest: requestVisitLogData.vehicles,
            requireFollowUp: requireFollowUp,
            followUpStatus: followUpStatus,
            phones: phones,
            emails: emails,
            selectedUser: selectedUser,
            existingVisitor: existingVisitor,
            selectToMeet: selectToMeet
        })
        
        // Use update if visitLog exists, otherwise create
        const apiCall = visitLog?.id 
            ? VisitorApi.updateVisitLog(visitLog.id, requestVisitLogData)
            : VisitorApi.createVisitLog(requestVisitLogData)
        
        apiCall.then(d => {
            console.log("Visit log saved successfully:", d)
            const responseData = d?.data
            // Check for error in response (new API format)
            if (responseData?.success === false) {
                const errorMessage = responseData.message || 'Unknown error'
                const errorCode = responseData.error_code || ""
                toast.error(`Error: ${errorMessage}${errorCode ? ` (${errorCode})` : ''}`)
                // Don't call onSuccess() - keep modal open
                return
            }
            // Success - show success message and close modal
            toast.success(`Visit log ${visitLog?.id ? 'updated' : 'created'} successfully`)
            onSuccess()
        }).catch(e => {
            console.error("Error saving visit log:", e)
            const errorData = e?.response?.data
            const errorMessage = errorData?.message || e?.message || "Unknown error"
            const errorCode = errorData?.error_code || ""
            toast.error(`Error ${visitLog?.id ? 'updating' : 'creating'} visit log: ${errorMessage}${errorCode ? ` (${errorCode})` : ''}`)
            // Don't call onSuccess() - keep modal open so user can fix the error
        })
    }

    return <div id="add-team-modal" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-y-auto">
            <div className="relative">
                <div className="p-6">
                    <div id="page-header" className="mb-6 flex justify-between">

                        <h1 className="text-2xl text-neutral-800 mb-2">{visitLog ? "Edit Visit Log" : "New Visit Log"}</h1>
                        <button
                            onClick={() => onClose()}
                            className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-white"
                        >
                            <i className="fa-solid fa-xmark text-2xl"></i>
                        </button>
                        {/* <p className="text-neutral-600">Collect detailed information for first-time visitors to facilitate follow-up and engagement.</p> */}
                    </div>
                    <form id="visitor-form" className="bg-white border border-neutral-200 rounded-lg shadow-sm" onSubmit={(e) => {
                        handleSubmit()
                        e.preventDefault()
                        return false;
                    }}>
                        {/* <!-- Main Section --> */}
                        <div id="main-section" className="p-6 border-b border-neutral-200">
                            <h2 className="text-lg text-neutral-800 mb-4">Main Information</h2>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="purpose" className="block text-sm text-black dark:text-white mb-1">
                                        Visit Purpose <span className="text-red-500">*</span>
                                    </label>
                                    <select id="purpose" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                        onChange={(e) => handleFormVisitData("purpose", e.target.value)} value={requestVisitLogData.purpose || -1} required>
                                        <option value={-1}>Select purpose</option>
                                        <option value={VisitPurpose.SERVICE}>Sunday Service</option>
                                        <option value={VisitPurpose.PRAYER}>Prayer Meeting</option>
                                        <option value={VisitPurpose.GOVERNMENT}>Government Official Visit</option>
                                        <option value={VisitPurpose.OTHER}>Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="visitor-type" className="block text-sm text-black dark:text-white mb-1">
                                        Visitor Type <span className="text-red-500">*</span>
                                    </label>
                                    <select id="visitor-type" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                        onChange={(e) => handleFormVisitData("visitor_type", e.target.value)} value={requestVisitLogData.visitor_type || -1} required>
                                        <option value={-1}>Select visitor type</option>
                                        <option value={VisitorType.GUEST}>Guest</option>
                                        <option value={VisitorType.PASTOR}>Pastor</option>
                                        <option value={VisitorType.STAFF}>Staff</option>
                                        <option value={VisitorType.VIP}>VIP</option>
                                        <option value={VisitorType.GOVT_OFFICIAL}>Government Official</option>
                                        <option value={VisitorType.PROSPECT}>Prospect</option>
                                        <option value={VisitorType.MEMBER}>Member</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="check-in" className="block text-sm text-black dark:text-white mb-1">
                                        Check-in Time <span className="text-red-500">*</span>
                                    </label>
                                    <input type="datetime-local" id="check-in" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors" onChange={(e) => {
                                        handleFormVisitData("checkin_time", e.target.value)
                                        handleFormVisitData("entry_time", e.target.value)
                                    }} value={typeof requestVisitLogData.checkin_time === 'string' ? requestVisitLogData.checkin_time : (typeof requestVisitLogData.entry_time === 'string' ? requestVisitLogData.entry_time : '')} required />

                                </div>
                                <div>
                                    <label htmlFor="check-out" className="block text-sm text-black dark:text-white mb-1">Check-out Time</label>
                                    <input type="datetime-local" id="check-out" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors" onChange={(e) => {
                                        handleFormVisitData("checkout_time", e.target.value)
                                        handleFormVisitData("exit_time", e.target.value)
                                    }} value={typeof requestVisitLogData.checkout_time === 'string' ? requestVisitLogData.checkout_time : (typeof requestVisitLogData.exit_time === 'string' ? requestVisitLogData.exit_time : '')} />
                                </div>
                                <div className="col-span-2">
                                    <label htmlFor="notes" className="block text-sm text-black dark:text-white mb-1">Notes</label>
                                    <textarea id="notes" rows={2} className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors" placeholder="Visit Details" onChange={(e) => handleFormVisitData("notes", e.target.value)} value={requestVisitLogData.notes || ''} />
                                </div>
                                <div className="col-span-2">
                                    <div className="flex items-center mb-3">
                                        <input 
                                            type="checkbox" 
                                            id="require-follow-up" 
                                            className="h-4 w-4 text-neutral-600 border-neutral-300 rounded focus:ring-neutral-500" 
                                            checked={requireFollowUp}
                                            onChange={(e) => setRequireFollowUp(e.target.checked)}
                                        />
                                        <label htmlFor="require-follow-up" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
                                            Require Follow Up
                                        </label>
                                    </div>
                                </div>
                                {requireFollowUp && (
                                    <div className="col-span-2">
                                        <label htmlFor="follow-up-status" className="block text-sm text-black dark:text-white mb-1">
                                            Follow Up Status <span className="text-red-500">*</span>
                                        </label>
                                        <select 
                                            id="follow-up-status" 
                                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                            value={followUpStatus}
                                            onChange={(e) => setFollowUpStatus(Number(e.target.value))}
                                            required
                                        >
                                            <option value={FollowUpStatus.VISITED}>Visited</option>
                                            <option value={FollowUpStatus.COMPLETED}>Completed</option>
                                            <option value={FollowUpStatus.RESCHEDULED}>Rescheduled</option>
                                            <option value={FollowUpStatus.NO_ANSWER}>No Answer</option>
                                            <option value={FollowUpStatus.UNREACHABLE}>Unreachable</option>
                                            <option value={FollowUpStatus.CANCELLED}>Cancelled</option>
                                            <option value={FollowUpStatus.LEAD}>Lead</option>
                                            <option value={FollowUpStatus.CONVERTED}>Converted</option>
                                        </select>
                                    </div>
                                )}
                                <div className="col-span-2">
                                    {/* <div className="flex items-center mb-3">
                                        <input type="checkbox" id="is-member" className="h-4 w-4 text-neutral-600 border-neutral-300 rounded focus:ring-neutral-500" />
                                        <label htmlFor="is-member" className="ml-2 block text-sm text-neutral-700">Is a member</label>
                                    </div> */}
                                    <div id="member-selection" className="flex items-center space-x-3">
                                        <button type="button" className="px-3 py-1.5 border border-neutral-300 rounded-md text-neutral-700 bg-white hover:bg-neutral-50 text-sm"
                                            onClick={() => {
                                                setShowSelectMember(true)

                                            }


                                            }>
                                            Select Member
                                        </button>
                                        {selectedUser ?
                                            <div className="flex items-center p-2 border border-neutral-200 rounded-md bg-neutral-50">
                                                <img src="https://api.dicebear.com/7.x/notionists/svg?scale=200&seed=789" alt="Selected member" className="w-8 h-8 rounded-full" />
                                                <div className="ml-2">
                                                    <div className="text-sm text-neutral-800">{selectedUser?.name}</div>
                                                    <div className="text-xs text-neutral-500">Regular Member</div>
                                                </div>
                                                <button type="button" className="text-neutral-400 hover:text-neutral-600" onClick={() => {
                                                    setSelectedUser(undefined)
                                                    handleFormVisitData("user_id", undefined)
                                                    handleFormVisitData("member_id", undefined)
                                                }}> <i className="fa-solid fa-times"></i></button>
                                            </div> : null
                                        }
                                    </div>
                                </div>
                                <div className="col-span-2">
                                    <div id="member-selection" className="flex items-center space-x-3">
                                        <button type="button" className="px-3 py-1.5 border border-neutral-300 rounded-md text-neutral-700 bg-white hover:bg-neutral-50 text-sm"
                                            onClick={() => setShowVisitor(true)}>
                                            Select Existing Visitor
                                        </button>
                                        {existingVisitor ?
                                            <div className="flex items-center p-2 border border-neutral-200 rounded-md bg-neutral-50">
                                                <img src="https://api.dicebear.com/7.x/notionists/svg?scale=200&seed=789" alt="Selected member" className="w-8 h-8 rounded-full" />
                                                <div className="ml-2">
                                                    <div className="text-sm text-neutral-800">{existingVisitor?.visitor_full_name}</div>
                                                    <div className="text-xs text-neutral-500">Visitor</div>
                                                </div>
                                                <button type="button" className="text-neutral-400 hover:text-neutral-600" onClick={() => {
                                                    setExistingVisitor(undefined)
                                                    handleFormVisitData("visitor_id", undefined)
                                                }}>
                                                    <i className="fa-solid fa-times"></i>
                                                </button>
                                            </div> : null
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* <!-- Whom to Meet Section --> */}
                        <div id="whom-to-meet-section" className="p-6 border-b border-neutral-200">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg text-neutral-800">Whom to Meet (Optional)</h2>
                            </div>
                            <div>
                                <button type="button" className="px-3 py-1.5 border border-neutral-300 rounded-md text-neutral-700 bg-white hover:bg-neutral-50 text-sm mb-3"
                                    onClick={() => { setShowSelectToMeet(true) }}>
                                    <i className="fa-solid fa-plus mr-1"></i> Add Person
                                </button>
                                <div className="space-y-2">
                                    {
                                        selectToMeet?.map((user, index) =>
                                            <div key={index} className="flex items-center p-2 border border-neutral-200 rounded-md bg-neutral-50">
                                                <img src="https://api.dicebear.com/7.x/notionists/svg?scale=200&seed=123" alt="Pastor John" className="w-8 h-8 rounded-full" />
                                                <div className="ml-2 flex-1">
                                                    <div className="text-sm text-neutral-800">{user.name}</div>
                                                    <div className="text-xs text-neutral-500">Senior Pastor</div>
                                                </div>
                                                <button type="button" className="text-neutral-400 hover:text-neutral-600" onClick={() => {
                                                    selectToMeet.splice(index, 1)
                                                    setSelectToMeet([...selectToMeet])
                                                }}>
                                                    <i className="fa-solid fa-times"></i>
                                                </button>
                                            </div>)
                                    }
                                </div>
                            </div>
                        </div>

                        {/* <!-- Vehicle Log Section --> */}
                        <div id="vehicle-log-section" className="p-6 border-b border-neutral-200">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg text-neutral-800">Vehicle Log</h2>
                            </div>
                            <button 
                                type="button" 
                                className="px-3 py-1.5 border border-neutral-300 rounded-md text-neutral-700 bg-white hover:bg-neutral-50 text-sm mb-3"
                                onClick={() => {
                                    setShowVehicleForm(true)
                                }}
                            >
                                <i className="fa-solid fa-plus mr-1"></i> Add Vehicle
                            </button>
                            <div className="space-y-2">
                                {vehicles.length === 0 ? (
                                    <p className="text-sm text-neutral-500 italic">No vehicles added</p>
                                ) : (
                                    vehicles.map((vehicle, index) => (
                                        <div key={index} className="p-3 border border-neutral-200 rounded-md bg-neutral-50">
                                            <div className="flex justify-between items-center mb-2">
                                                <div className="text-neutral-800 font-medium">
                                                    {vehicle.make || vehicle.type || 'Vehicle'}
                                                </div>
                                                <button 
                                                    type="button" 
                                                    className="text-neutral-400 hover:text-neutral-600"
                                                    onClick={() => {
                                                        const newVehicles = [...vehicles]
                                                        newVehicles.splice(index, 1)
                                                        setVehicles(newVehicles)
                                                    }}
                                                >
                                                    <i className="fa-solid fa-times"></i>
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3 text-sm">
                                                <div>
                                                    <span className="text-neutral-500">License Plate:</span>
                                                    <span className="ml-1 text-neutral-700 font-medium">{vehicle.reg_no}</span>
                                                </div>
                                                <div>
                                                    <span className="text-neutral-500">Type:</span>
                                                    <span className="ml-1 text-neutral-700">{vehicle.type}</span>
                                                </div>
                                                {vehicle.color && (
                                                    <div>
                                                        <span className="text-neutral-500">Color:</span>
                                                        <span className="ml-1 text-neutral-700">{vehicle.color}</span>
                                                    </div>
                                                )}
                                                {vehicle.make && (
                                                    <div>
                                                        <span className="text-neutral-500">Make:</span>
                                                        <span className="ml-1 text-neutral-700">{vehicle.make}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* <!-- Visitor Details Section --> */}
                        <fieldset disabled={selectedUser != null || existingVisitor != null}>

                            <div id="visitor-details-section" className="p-6 border-b border-neutral-200">
                                <h2 className="text-lg text-neutral-800 mb-4">Visitor Details</h2>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="full-name" className="block text-sm text-black dark:text-white mb-1">
                                            Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <input type="text" id="full-name" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                            onChange={(e) => handleFormVisitorData("visitor_full_name", e.target.value)} value={(requestVisitorData as any)['visitor_full_name']} required />
                                    </div>
                                    <div>
                                        <label htmlFor="designation" className="block text-sm text-black dark:text-white mb-1">
                                            Designation/Profession <span className="text-red-500">*</span>
                                        </label>
                                        <select id="designation" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                            onChange={(e) => {
                                                const value = Number(e.target.value)
                                                handleFormVisitorData("designation_profession", value)
                                                // Clear other_designation_profession if not "Other"
                                                if (value !== Profession.OTHER) {
                                                    handleFormVisitorData("other_designation_profession", "")
                                                }
                                            }} 
                                            value={(requestVisitorData as any)['designation_profession'] !== undefined ? (requestVisitorData as any)['designation_profession'] : -1}
                                            required>
                                            <option value={-1}>Select profession</option>
                                            <option value={Profession.TEACHER}>Teacher</option>
                                            <option value={Profession.DOCTOR}>Doctor</option>
                                            <option value={Profession.ENGINEER}>Engineer</option>
                                            <option value={Profession.BUSINESS}>Business Owner</option>
                                            <option value={Profession.STUDENT}>Student</option>
                                            <option value={Profession.OTHER}>Other</option>
                                        </select>
                                    </div>
                                    <div id="other-designation-container">
                                        <label htmlFor="other-designation" className="block text-sm text-black dark:text-white mb-1">
                                            Other Designation/Profession {(requestVisitorData as any)['designation_profession'] === Profession.OTHER && <span className="text-red-500">*</span>}
                                        </label>
                                        <input 
                                            disabled={(requestVisitorData as any)['designation_profession'] !== Profession.OTHER} 
                                            type="text" 
                                            id="other-designation" 
                                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500 dark:disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors" 
                                            onChange={(e) => handleFormVisitorData("other_designation_profession", e.target.value)}
                                            value={(requestVisitorData as any)['other_designation_profession'] || ''}
                                            placeholder={(requestVisitorData as any)['designation_profession'] === Profession.OTHER ? "Enter designation/profession" : "Select 'Other' above to enable"}
                                            required={(requestVisitorData as any)['designation_profession'] === Profession.OTHER}
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="block text-sm text-neutral-700">
                                                Phone Numbers <span className="text-red-500">*</span>
                                                <span className="text-xs text-gray-500 ml-2">(At least one phone or email required)</span>
                                            </label>
                                            <button type="button" className="text-sm text-neutral-600 hover:text-neutral-800" onClick={() => setPhones([...phones, ''])}>
                                                <i className="fa-solid fa-plus mr-1"></i> Add Phone
                                            </button>
                                        </div>
                                        <div className="space-y-2">
                                            {/* <div className="flex items-center">
                                                <input type="tel" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors" value="+1234567890" />
                                                <button type="button" className="ml-2 text-neutral-400 hover:text-neutral-600">
                                                    <i className="fa-solid fa-times"></i>
                                                </button>
                                            </div> */}
                                            {phones.map((p, i) =>
                                                <div key={i} className="flex items-center">
                                                    <input type="tel" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors" onChange={ev => { phones[i] = ev.target.value; setPhones([...phones]) }} value={p} />
                                                    {i > 0 ? <button type="button" className="ml-2 text-neutral-400 hover:text-neutral-600" onClick={() => {
                                                        phones.splice(i, 1)
                                                        setPhones([...phones])
                                                    }}>
                                                        <i className="fa-solid fa-times"></i>
                                                    </button> : null}
                                                </div>)}
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="block text-sm text-neutral-700">
                                                Email Addresses
                                                <span className="text-xs text-gray-500 ml-2">(At least one phone or email required)</span>
                                            </label>
                                            <button type="button" className="text-sm text-neutral-600 hover:text-neutral-800"
                                                onClick={() => setEmails([...emails, ''])} >
                                                <i className="fa-solid fa-plus mr-1"></i> Add Email
                                            </button>
                                        </div>
                                        {emails.map((e, i) => <div key={i} className="flex items-center">
                                            <input type="email" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors" onChange={ev => { emails[i] = ev.target.value; setEmails([...emails]) }} value={e} />
                                            {i > 0 ? <button type="button" className="ml-2 text-neutral-400 hover:text-neutral-600" onClick={() => {
                                                emails.splice(i, 1)
                                                setEmails([...emails])
                                            }}>
                                                <i className="fa-solid fa-times"></i>
                                            </button> : null}
                                        </div>)}
                                    </div>
                                </div>
                            </div>

                            {/* <!-- Information Source Section --> */}
                            <div id="information-source-section" className="p-6 border-b border-neutral-200">
                                <h2 className="text-lg text-neutral-800 mb-4">Information Source</h2>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="source" className="block text-sm text-black dark:text-white mb-1">
                                            Source of Information <span className="text-red-500">*</span>
                                        </label>
                                        <select id="source" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                            onChange={e => {
                                                const value = Number(e.target.value)
                                                handleFormVisitorData('source_of_information', value)
                                                // Clear other_source_specify if not "Other"
                                                if (value !== InformationSource.OTHER) {
                                                    handleFormVisitorData("other_source_specify", "")
                                                }
                                            }}
                                            value={(requestVisitorData as any)['source_of_information'] !== undefined ? (requestVisitorData as any)['source_of_information'] : -1}
                                            required>
                                            <option value={-1}>Select source</option>
                                            <option value={InformationSource.MEMBER}>Church Member</option>
                                            <option value={InformationSource.SOCIAL_NETWORK}>Social Network</option>
                                            <option value={InformationSource.YOUTUBE}>YouTube</option>
                                            <option value={InformationSource.FACEBOOK}>Facebook</option>
                                            <option value={InformationSource.TV}>Television</option>
                                            <option value={InformationSource.OTHER}>Other</option>
                                        </select>
                                    </div>
                                    <div id="other-source-container">
                                        <label htmlFor="other-source" className="block text-sm text-black dark:text-white mb-1">
                                            Other Source (Specify) {(requestVisitorData as any)['source_of_information'] === InformationSource.OTHER && <span className="text-red-500">*</span>}
                                        </label>
                                        <input 
                                            disabled={(requestVisitorData as any)['source_of_information'] !== InformationSource.OTHER} 
                                            type="text" 
                                            id="other-source" 
                                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500 dark:disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors" 
                                            onChange={(e) => handleFormVisitorData("other_source_specify", e.target.value)}
                                            value={(requestVisitorData as any)['other_source_specify'] || ''}
                                            placeholder={(requestVisitorData as any)['source_of_information'] === InformationSource.OTHER ? "Enter source of information" : "Select 'Other' above to enable"}
                                            required={(requestVisitorData as any)['source_of_information'] === InformationSource.OTHER}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* <!-- Current Church Details Section --> */}
                            <div id="church-details-section" className="p-6 border-b border-neutral-200">
                                <h2 className="text-lg text-neutral-800 mb-4">Current Church Details</h2>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="current-church" className="block text-sm text-black dark:text-white mb-1">Current Church</label>
                                        <input type="text" id="current-church" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                            onChange={(e) => handleFormVisitorData("current_church", e.target.value)} value={(requestVisitorData as any)['current_church'] || ''} />
                                    </div>
                                    <div>
                                        <label htmlFor="church-pastors" className="block text-sm text-black dark:text-white mb-1">Church Pastor(s)</label>
                                        <input type="text" id="church-pastors" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                            onChange={(e) => {
                                                handleFormVisitorData("church_pastors", e.target.value)
                                                handleFormVisitorData("current_church_pastor", e.target.value)
                                            }} 
                                            value={(requestVisitorData as any)['church_pastors'] || (requestVisitorData as any)['current_church_pastor'] || ''} />

                                    </div>
                                    <div className="col-span-2">
                                        <div className="flex items-center mb-3">
                                            <input type="checkbox" id="contact-for-prayer" className="h-4 w-4 text-neutral-600 border-neutral-300 rounded focus:ring-neutral-500"
                                                onChange={(e) => handleFormVisitorData("contact_preference", e.target.checked ? 1 : 0)} 
                                                checked={(requestVisitorData as any)['contact_preference'] === 1}
                                            />
                                            <label htmlFor="contact-for-prayer" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">Contact for prayer</label>
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <label htmlFor="prayer-request" className="block text-sm text-black dark:text-white mb-1">Prayer Request</label>
                                        <textarea id="prayer-request" rows={3} className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                            onChange={(e) => handleFormVisitorData("prayer_request", e.target.value)} />

                                    </div>
                                </div>
                            </div>

                            {/* <!-- Referrer Details Section --> */}
                            <div id="referrer-details-section" className="p-6 border-b border-neutral-200">
                                <h2 className="text-lg text-neutral-800 mb-4">Referrer Details</h2>
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="col-span-2">
                                        {/* <div className="flex items-center mb-3">
                                            <input type="checkbox" id="is-referrer-member" className="h-4 w-4 text-neutral-600 border-neutral-300 rounded focus:ring-neutral-500" />
                                            <label htmlFor="is-referrer-member" className="ml-2 block text-sm text-neutral-700">Referrer is a member</label>
                                        </div> */}
                                        <div id="referrer-member-selection" className="flex items-center space-x-3 mb-4">
                                            <button type="button" className="px-3 py-1.5 border border-neutral-300 rounded-md text-neutral-700 bg-white hover:bg-neutral-50 text-sm"
                                                onClick={() => {
                                                    setShowReferrer(true)
                                                }}>
                                                Select Referrer Member
                                            </button>
                                            {referrer &&
                                                <div className="flex items-center p-2 border border-neutral-200 rounded-md bg-neutral-50">
                                                    <img src="https://api.dicebear.com/7.x/notionists/svg?scale=200&seed=456" alt="Selected referrer" className="w-8 h-8 rounded-full" />
                                                    <div className="ml-2">
                                                        <div className="text-sm text-neutral-800">{referrer.name}</div>
                                                        <div className="text-xs text-neutral-500">Regular Member</div>
                                                    </div>
                                                    <button type="button" className="text-neutral-400 hover:text-neutral-600" onClick={() => {
                                                        setReferrer(undefined)
                                                    }}>
                                                        <i className="fa-solid fa-times"></i>
                                                    </button>
                                                </div>}
                                        </div>
                                    </div>
                                    <fieldset disabled={referrer != null}>
                                        <div>
                                            <label htmlFor="referrer-name" className="block text-sm text-black dark:text-white mb-1">Referrer Full Name</label>
                                            <input type="text" id="referrer-name" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                                onChange={(e) => handleFormVisitorData("referrer_full_name", e.target.value)} value={(requestVisitorData as any)['referrer_full_name'] || ''} />
                                        </div>
                                        <div>
                                            <label htmlFor="referrer-phone" className="block text-sm text-black dark:text-white mb-1">Referrer Phone</label>
                                            <input type="tel" id="referrer-phone" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                                onChange={(e) => handleFormVisitorData("referrer_phone", e.target.value)} value={(requestVisitorData as any)['referrer_phone'] || ''} />
                                        </div>
                                        <div className="col-span-2">
                                            <label htmlFor="referrer-address" className="block text-sm text-black dark:text-white mb-1">Referrer Address</label>
                                            <input type="text" id="referrer-address" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                                                onChange={(e) => handleFormVisitorData("referrer_address", e.target.value)} value={(requestVisitorData as any)['referrer_address'] || ''} />
                                        </div>
                                    </fieldset>
                                </div>
                            </div>
                        </fieldset>

                        {/* <!-- Form Actions --> */}
                        <div id="form-actions" className="p-6 bg-neutral-50 flex justify-end space-x-3 rounded-b-lg">
                            <button type="button" className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                                onClick={() => { onClose() }}>
                                Cancel
                            </button>
                            {/* <button type="button" className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 bg-white hover:bg-neutral-50">
                                Save Draft
                            </button> */}
                            <button type="submit" className="px-4 py-2 bg-neutral-800 text-white rounded-md hover:bg-neutral-700">
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        {
            showSelectMember && <UserManagementPage onSelectUsers={([user]) => {
                setShowSelectMember(false)
                setSelectedUser(user)
                if (user) {
                    handleFormVisitData("user_id", user.id)
                    handleFormVisitData("member_id", user.id) // Also set alias
                    handleFormVisitData("is_member", true)
                } else {
                    handleFormVisitData("is_member", false)
                }

            }} onClose={() => {
                setShowSelectMember(false)
                setExistingVisitor(undefined)
            }} />
        }

        {
            showSelectToMeet && <UserManagementPage onSelectUsers={(users) => {
                setShowSelectToMeet(false)
                setSelectToMeet(users)

                handleFormVisitData("to_meet_user_ids", users.map(u => u.id))

            }} onClose={() => setShowSelectToMeet(false)} />
        }

        {
            showReferrer && <UserManagementPage onSelectUsers={([user]) => {
                setShowReferrer(false)
                setReferrer(user)
                if (user) {
                    handleFormVisitorData("is_referrer_member", true)
                    handleFormVisitorData("referrer_member_user_id", user.id)
                } else {
                    handleFormVisitorData("is_referrer_member", false)
                }
            }} onClose={() => setShowReferrer(false)} />
        }
        {
            showVisitor && <VistorList onClose={() => setShowVisitor(false)} onSelect={v => {
                setExistingVisitor(v)
                if (v) {
                    handleFormVisitData("visitor_id", v.id)
                }
                setSelectedUser(undefined)
            }} />
        }

        {/* Vehicle Form Modal */}
        {showVehicleForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-neutral-800">
                            Add Vehicle
                        </h2>
                        <button
                            onClick={() => {
                                setShowVehicleForm(false)
                            }}
                            className="text-neutral-600 hover:text-neutral-800"
                        >
                            <i className="fa-solid fa-xmark text-2xl"></i>
                        </button>
                    </div>
                    <VehicleForm
                        vehicle={undefined}
                        onSave={(vehicleData) => {
                            setVehicles([...vehicles, vehicleData])
                            setShowVehicleForm(false)
                        }}
                        onClose={() => {
                            setShowVehicleForm(false)
                        }}
                        key={showVehicleForm ? 'new' : 'closed'}
                    />
                </div>
            </div>
        )}

    </div>
}